/**
 * Human-readable "next scheduled run" formatting (v1.13.5).
 *
 * Pure, side-effect-free so it is trivially unit-testable. The agents list shows each
 * scheduled agent's next expected fire time (`AgentView.nextRunAt`, an ISO datetime the
 * scheduler maintains). We render it in the VIEWER's local time, relative when it is
 * soon (within a day) and an absolute date-time otherwise — plus a full local timestamp
 * for the `title` tooltip so the exact instant is always one hover away.
 */

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

export interface NextRunLabel {
  /** Short, human label for the row, e.g. "in 3h", "Tomorrow 09:00", "Jul 20, 09:00". */
  label: string;
  /** Full local timestamp for the `title` tooltip, e.g. "Sat, Jul 19, 2026, 09:00". */
  title: string;
}

/** `HH:MM` in the viewer's local time (24h, zero-padded). */
function localHhmm(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/** Whole local-calendar days from `a` to `b` (b − a), ignoring time-of-day. */
function calendarDayDelta(a: Date, b: Date): number {
  const da = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const db = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((db.getTime() - da.getTime()) / MS_PER_DAY);
}

/**
 * Format a next-run ISO datetime relative to `now` (defaults to the current instant).
 * Returns `null` for a missing/invalid value so the caller can render nothing.
 *
 * Bands (all in the viewer's local timezone):
 *   - past / due:        "due now"
 *   - < 60 min:          "in Nm"           (at least "in 1m")
 *   - < 24 h:            "in Nh"
 *   - tomorrow:          "Tomorrow HH:MM"
 *   - else:              "Mon DD, HH:MM"   (e.g. "Jul 20, 09:00")
 */
export function formatNextRun(
  iso: string | null | undefined,
  now: Date = new Date(),
): NextRunLabel | null {
  if (!iso) return null;
  const target = new Date(iso);
  const ms = target.getTime();
  if (Number.isNaN(ms)) return null;

  const title = target.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const delta = ms - now.getTime();
  let label: string;
  if (delta <= 0) {
    label = 'due now';
  } else if (delta < MS_PER_HOUR) {
    label = `in ${Math.max(1, Math.round(delta / MS_PER_MINUTE))}m`;
  } else if (delta < MS_PER_DAY) {
    label = `in ${Math.round(delta / MS_PER_HOUR)}h`;
  } else if (calendarDayDelta(now, target) === 1) {
    label = `Tomorrow ${localHhmm(target)}`;
  } else {
    const date = target.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    label = `${date}, ${localHhmm(target)}`;
  }

  return { label, title };
}
